import type { ListingOffer } from "./types";

/**
 * What an SBA offer's seller note still owes at the end of the 10-year
 * projection -- the amount the 10-yr wealth figure subtracts.
 *
 * Only what's STILL OWED. Payments inside the window (a subordinated
 * note's amortization, or a full-standby note released when the SBA
 * loan is paid off) already came out of take-home, so subtracting the
 * whole note here would count it twice.
 *
 * Written by the projection engine onto the offer
 * (seller_note_balance_end). Before an offer's first run on the
 * seller-note engine it's null, and this falls back to the previous
 * rule: the note compounded at its rate for 10 years with nothing paid
 * -- which is also exactly what the engine produces for a full-standby
 * note on an SBA loan longer than the window.
 */
export function sellerNoteOwedAtEnd(
  offer: Pick<ListingOffer, "seller_note_balance_end" | "seller_financing_standby" | "cash_structure_seller_financing_rate"> | null | undefined,
): number {
  if (!offer) return 0;
  if (offer.seller_note_balance_end != null) return offer.seller_note_balance_end;
  if (offer.seller_financing_standby == null) return 0;
  return offer.seller_financing_standby * Math.pow(1 + (offer.cash_structure_seller_financing_rate ?? 0), 10);
}

/** Which rule governs an SBA seller note. Mirrors offer_projections.seller_note_mode. */
export function sellerNoteMode(downPayment: number, totalProjectCost: number): "full_standby" | "subordinated" {
  if (!totalProjectCost || totalProjectCost <= 0) return "full_standby";
  return downPayment + 0.01 >= totalProjectCost * 0.1 ? "subordinated" : "full_standby";
}
